// Printable question cards for the admin (BRIEFS C11.3): the test review page
// (/admin/test-review/:testId) and "Print questions by ID" (/admin/v2/print).
// Each card: "Q <id>" (selectable), PYQ tag, question, options, the student's answer (test
// review only), the correct answer, the explanation and the notes reference titles. Every
// colour also has a text label, so a black-and-white print still reads right.
import React from 'react';
import MathHtml from '../../../components/shared/MathHtml';

const LETTERS = ['A', 'B', 'C', 'D'];
const INK = '#0F172A';
const MUTED = '#475569';
const LINE = '#E2E8F0';

// Question text from the old bank: html, or plain text with line breaks.
function toHtml(s) {
  const t = s || '';
  return t.indexOf('<') >= 0 ? t : t.split('\n').join('<br>');
}

export const PRINT_CSS = `
.pq-card{break-inside:avoid;page-break-inside:avoid}
.pq-expl p{margin:0 0 6px}
.pq-expl ul,.pq-expl ol{margin:4px 0 6px;padding-left:22px}
.pq-qid{user-select:text;-webkit-user-select:text}
@media print{
  .no-print{display:none !important}
  body{background:#fff !important}
  .pq-page{padding:0 !important;max-width:none !important}
  .pq-card{border-width:1px !important}
  @page{margin:14mm 12mm}
}
`;

export function PyqTag({ pyq }) {
  if (!pyq || !pyq.label) return null;
  return (
    <span data-testid="pq-pyq" style={{
      fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 999,
      background: '#FDF3E3', color: '#6B3A06', border: '1px solid #F1D3A1',
    }}>PYQ: {pyq.label}</span>
  );
}

function statusOf(q) {
  if (!q.is_attempted) return { text: 'Not answered', bg: '#F1F5F9', fg: MUTED, border: LINE };
  if (q.is_correct === true) return { text: 'Correct', bg: '#D1FAE5', fg: '#065F46', border: '#10B981' };
  if (q.is_correct === false) return { text: 'Wrong', bg: '#FEE2E2', fg: '#991B1B', border: '#EF4444' };
  return { text: 'Answered · not graded yet', bg: '#EEF2FF', fg: '#3730A3', border: '#A5B4FC' };
}

function refLine(refs) {
  return (refs || []).map((r) => [r.number, r.title].filter(Boolean).join(' ')).join('  ·  ');
}

// q: print form from the API. withStudent: show the student's answer and the status.
// explanationText: the old page's raw explanation (shown as text) when no html is there.
export function PrintQuestion({ q, index, withStudent = false, explanationText }) {
  const st = withStudent ? statusOf(q) : null;
  const mine = withStudent ? q.student_answer : null;
  const correct = q.correct_option;
  const refs = q.notes_refs;
  return (
    <div className="pq-card" data-testid="pq-card" data-qid={q.question_id} style={{
      border: `2px solid ${st ? st.border : LINE}`, borderRadius: 12, overflow: 'hidden',
      background: '#fff', color: INK,
    }}>
      <div style={{
        padding: '8px 14px', background: st ? st.bg : '#F8FAFC', borderBottom: `1px solid ${LINE}`,
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
      }}>
        <span style={{ fontWeight: 700, color: MUTED }}>{index != null ? `Question ${index}` : ''}</span>
        <span className="pq-qid" data-testid="pq-qid" title="Question ID" style={{
          fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#3730A3',
          background: '#EEF2FF', padding: '2px 8px', borderRadius: 6,
        }}>Q {q.question_id}</span>
        <PyqTag pyq={q.pyq} />
        {q.chapter && <span style={{ fontSize: 12, color: MUTED }}>{q.chapter}</span>}
        {st && (
          <span data-testid="pq-status" style={{
            marginLeft: 'auto', fontSize: 12, fontWeight: 700, padding: '2px 10px',
            borderRadius: 999, background: '#fff', color: st.fg, border: `1px solid ${st.border}`,
          }}>{st.text}</span>
        )}
      </div>
      <div style={{ padding: 14 }}>
        {q.passage_text && (
          <div style={{
            background: '#F8FAFC', border: `1px solid ${LINE}`, borderRadius: 8,
            padding: '8px 12px', marginBottom: 10, fontSize: 13, lineHeight: 1.7,
          }}>
            <MathHtml html={toHtml(q.passage_text)} />
          </div>
        )}
        <MathHtml html={toHtml(q.question_text)} style={{ fontSize: 15, lineHeight: 1.7, fontWeight: 500, marginBottom: 10 }} />
        {q.question_image_url && <img src={q.question_image_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 10 }} />}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 6, marginBottom: 10 }}>
          {LETTERS.map((L) => {
            const val = q[`option_${L.toLowerCase()}`];
            const img = q[`option_${L.toLowerCase()}_image_url`];
            if (!val && !img) return null;
            const isCorrect = correct === L;
            const isMine = mine === L;
            let border = LINE; let bg = '#fff';
            if (isCorrect) { border = '#10B981'; bg = '#F0FDF4'; }
            if (isMine && !isCorrect) { border = '#EF4444'; bg = '#FFF5F5'; }
            const tags = [];
            if (isCorrect) tags.push('Correct answer');
            if (isMine) tags.push(isCorrect ? 'Student’s answer' : 'Student’s answer · wrong');
            return (
              <div key={L} data-testid={`pq-opt-${L}`} style={{
                display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 10px',
                borderRadius: 8, border: `1.5px solid ${border}`, background: bg, fontSize: 13.5,
              }}>
                <b style={{ flexShrink: 0 }}>{L}.</b>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <MathHtml tag="span" html={toHtml(val)} />
                  {img && <img src={img} alt="" style={{ display: 'block', maxWidth: '100%', marginTop: 4 }} />}
                </span>
                {tags.length > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: isCorrect ? '#065F46' : '#991B1B', whiteSpace: 'nowrap' }}>
                    {tags.join(' · ')}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <div data-testid="pq-answer-line" style={{ fontSize: 13.5, marginBottom: 10 }}>
          <b>Correct answer: {correct || '—'}</b>
          {withStudent && (
            <span style={{ color: MUTED }}>
              {'  ·  '}Student’s answer: {mine || 'not answered'}
              {q.shown_answer ? ` (shown on the student’s screen as ${q.shown_answer})` : ''}
            </span>
          )}
        </div>
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', marginBottom: 4 }}>EXPLANATION</div>
          {q.explanation ? (
            <MathHtml className="pq-expl" html={q.explanation} style={{ fontSize: 13, lineHeight: 1.7, color: '#78350F' }} />
          ) : explanationText ? (
            <div style={{ fontSize: 13, lineHeight: 1.7, color: '#78350F', whiteSpace: 'pre-wrap' }}>{explanationText}</div>
          ) : (
            <div style={{ fontSize: 13, color: MUTED }}>No explanation yet</div>
          )}
          {q.explanation_image_url && <img src={q.explanation_image_url} alt="" style={{ maxWidth: '100%', marginTop: 6 }} />}
        </div>
        <div data-testid="pq-refs" style={{ fontSize: 13, color: refs && refs.length ? INK : MUTED }}>
          <b>Notes reference: </b>{refs && refs.length ? refLine(refs) : 'No notes reference yet'}
        </div>
      </div>
    </div>
  );
}

export function StatGrid({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 10, marginBottom: 24 }}>
      {items.map((s) => (
        <div key={s.label} style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 12, padding: 12, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: s.color || INK }}>{s.value}</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

export const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};
